<template lang="pug">
div.grid(
  ref="root"
  v-touch="{ start: startTouch, end: endTouch }"
)
  div.grid__scroll-x-wrapper.fill-height(
    ref="scrollXWrapper"
    tabindex="0"
  )
    div.grid__content(:style="contentStyle")
      slot
</template>

<script setup lang="ts">
/**
 * Component to display items in grid form, and scroll inside the grid.
 */

import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useElementSize } from "@vueuse/core";
import { debounceTimeout } from "~/units/debounceTimeout";
import { minMax } from "~/units/math";
import { wait } from "~/units/time";
import type { Dict } from "~/types";

interface Props {
  /**
   * The index of the currently selected column.
   */
  currentColumn?: number;

  /**
   * The total number of columns shown.
   */
  numColumns: number;
}

const props = withDefaults(defineProps<Props>(), {
  currentColumn: 0,
});

interface Emits {
  (e: "update:currentColumn", newColumnIndex: number): void;
}

const emit = defineEmits<Emits>();

const touching = ref<boolean>(false);
const programmaticallyScrolling = ref<boolean>(false);
const scrollXWrapper = ref<HTMLElement | null>(null);
const root = ref(null);
const { width, height } = useElementSize(root);

/**
 * Called when a touch is starting, similar to keydown
 */
function startTouch() {
  touching.value = true;
}

/**
 * Called when a touch in ending, similar to keyup
 */
function endTouch() {
  touching.value = false;
}

/**
 * Called by debouncer after scrolling has ended.
 */
function scrollEnd() {
  if (programmaticallyScrolling.value) {
    // Something is scrolling programmatically. Ignore this event, but unpause the scroll event listener to
    // allow user scroll events to be handled again.
    programmaticallyScrolling.value = false;
    return;
  }

  if (touching.value) {
    // User is still touching the screen. Ignore the event.
    return;
  }

  const element = scrollXWrapper.value;
  if (!element) {
    return;
  }

  // Figure out which column we scrolled to, and update the model.
  const scrollLeftMax = element.scrollWidth - element.clientWidth;
  currentColumnModel.value = Math.round(
    (element.scrollLeft / scrollLeftMax) * (props.numColumns - 1),
  );
}

const scrollEndDebounced = debounceTimeout(
  () => {
    scrollEnd();
  },
  100,
  false,
);

/**
 * Scroll to show a certain column of the grid.
 */
function scrollToColumn(newColumnIndex: number): void {
  const element = scrollXWrapper.value;
  if (!element) {
    return;
  }

  // Calculate the scroll.
  const clientWidth = element.clientWidth;
  const columnWidth = clientWidth / nVisibleColumns.value;
  const left = newColumnIndex * columnWidth;

  // Don't scroll if not necessary. This prevents running event handlers unnecessarily.
  if (left !== element.scrollLeft) {
    // Pause the scroll event listener to prevent infinite loops.
    // This flag should be cleared by the scrollEnd handler.
    programmaticallyScrolling.value = true;
    // Scroll programmatically.
    element.scroll({
      left,
      behavior: "smooth",
    });
  }
}

onMounted(() => {
  scrollXWrapper.value?.addEventListener("scroll", scrollEndDebounced);

  // Scroll to the selected column after mounting.
  nextTick().then(() => {
    wait(500).then(() => {
      scrollToColumn(props.currentColumn);
    });
  });
});

const currentColumnModel = computed<number>({
  get() {
    return props.currentColumn;
  },
  set(val) {
    // Only emit if something actually changed.
    if (val !== props.currentColumn) {
      emit("update:currentColumn", val);
    }
  },
});

/**
 * Styles to apply to the element containing the grid items.
 */
const contentStyle = computed<Dict<string | number>>(() => {
  return {
    "--num-columns": props.numColumns,
    "--n-parallel": nVisibleColumns.value,
  };
});

/**
 * The number of visible columns.
 */
const nVisibleColumns = computed<number>(() => {
  return minMax(1, props.numColumns, Math.floor(width.value / 300));
});

watch(
  () => props.currentColumn,
  () => {
    nextTick(() => {
      scrollToColumn(props.currentColumn);
    });
  },
);
</script>

<style lang="scss" scoped>
.grid__scroll-x-wrapper {
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-snap-stop: always;
}

.grid__content {
  --col-width: calc(100% / var(--n-parallel));
  display: grid;
  grid-template-columns: repeat(var(--num-columns, 1), var(--col-width));

  & > * {
    scroll-snap-align: start;
    scroll-snap-stop: always;
  }
}
</style>
